/**
 * This is a wrapper component for different Layouts.
 * Navigational 'aside' content should be added to this wrapper.
 */
import React, { useEffect, useRef } from 'react';
import { func, node, number, string, shape } from 'prop-types';
import { compose } from 'redux';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { withViewport } from '../../../util/uiHelpers';

import { TabNav } from '../../../components';
import ExternalBookingEntry from '../../../containers/ExternalBookingEntry/ExternalBookingEntry';

import { createGlobalState } from './hookGlobalState';

import css from './LayoutSideNavigation.module.css';
import { connect } from 'react-redux';
import { ensureUser } from '../../../util/data';
import { userHasAnnualStats } from '../../../util/userAnnualStats';
import { userCanSeeLegacyReservaAntiguas } from '../../../util/legacyReservaAntiguasAccess';
import { CUSTOMER, PROVIDER } from '../../../util/types';
import { getMarketplaceEntities } from '../../../ducks/marketplaceData.duck';
import { loadData as loadOnholdReservations } from '../../../containers/OnholdListingPage/OnholdingListingsPage.duck';

const MAX_HORIZONTAL_NAV_SCREEN_WIDTH = 1023;

// Add global state for tab scrolling effect
const initialScrollState = { scrollLeft: 0 };
const { useGlobalState } = createGlobalState(initialScrollState);

// Horizontal scroll animation using element.scrollTo()
const scrollToTab = (currentPage, scrollLeft, setScrollLeft) => {
  const el = document.querySelector(`#${currentPage}Tab`);

  if (el) {
    // el.scrollIntoView doesn't work with Safari and it considers vertical positioning too.
    // This scroll behaviour affects horizontal scrolling only
    // and it expects that the immediate parent element is scrollable.
    const parent = el.parentElement;
    const parentRect = parent.getBoundingClientRect();
    const maxScrollDistance = parent.scrollWidth - parentRect.width;

    const hasParentScrolled = parent.scrollLeft > 0;
    const scrollPositionCurrent = hasParentScrolled ? parent.scrollLeft : scrollLeft;

    const tabRect = el.getBoundingClientRect();
    const diffLeftBetweenTabAndParent = tabRect.left - parentRect.left;
    const tabScrollPosition = parent.scrollLeft + diffLeftBetweenTabAndParent;

    const scrollPositionNew =
      tabScrollPosition > maxScrollDistance
        ? maxScrollDistance
        : parent.scrollLeft + diffLeftBetweenTabAndParent;

    const needsSmoothScroll = scrollPositionCurrent !== scrollPositionNew;

    if (!hasParentScrolled || (hasParentScrolled && needsSmoothScroll)) {
      // Ensure that smooth scroll animation uses old position as starting point after navigation.
      parent.scrollTo({ left: scrollPositionCurrent });
      // Scroll to new position
      parent.scrollTo({ left: scrollPositionNew, behavior: 'smooth' });
    }
    // Always keep track of new position (even if smooth scrolling is not applied)
    setScrollLeft(scrollPositionNew);
  }
};

const LayoutWrapperReservationsSideNavComponent = props => {
  const intl = useIntl();
  const [scrollLeft, setScrollLeft] = useGlobalState('scrollLeft');
  const scrollLeftRef = useRef(scrollLeft);
  scrollLeftRef.current = scrollLeft;
  const {
    currentUser,
    currentPage,
    onholdReservationCount,
    onFetchOnholdReservations,
    viewport,
  } = props;

  useEffect(() => {
    const user = ensureUser(currentUser);
    if (!user?.id) {
      return;
    }
    const userType = user.attributes?.profile?.publicData?.userType ?? CUSTOMER;
    const tab = userType === PROVIDER ? 'sales' : 'orders';
    const search = typeof window !== 'undefined' ? window.location.search || '' : '';
    onFetchOnholdReservations(tab, search);
  }, [
    currentUser?.id?.uuid,
    currentUser?.attributes?.profile?.publicData?.userType,
    currentPage,
    onFetchOnholdReservations,
  ]);

  useEffect(() => {
    let scrollTimeout = null;

    const { width } = viewport;
    const hasViewport = width > 0;
    const hasHorizontalTabLayout = hasViewport && width <= MAX_HORIZONTAL_NAV_SCREEN_WIDTH;

    // Scroll to the active tab when the page or viewport changes — not on every render.
    // Re-running on unrelated updates (e.g. onhold count badge) was snapping the slider
    // back and blocking access to tabs the user had scrolled to.
    if (hasHorizontalTabLayout) {
      scrollTimeout = window.setTimeout(() => {
        scrollToTab(currentPage, scrollLeftRef.current, setScrollLeft);
      }, 300);
    }

    return () => {
      const el = document.querySelector(`#${currentPage}Tab`);
      if (el?.parentElement) {
        setScrollLeft(el.parentElement.scrollLeft);
      }
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [currentPage, viewport.width]);

  const ensuredUser = ensureUser(currentUser);
  const userType =
    (ensuredUser && ensuredUser?.id && ensuredUser?.attributes?.profile?.publicData?.userType) ??
    CUSTOMER;
  const isProvider = userType && userType == PROVIDER;

  const onholdTabLabel =
    onholdReservationCount > 0 ? (
      <span className={css.reservationsTabLabel}>
        <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.onHoldListingsTabTitle" />
        <span
          className={css.reservationsOnholdBadge}
          aria-label={intl.formatMessage(
            { id: 'LayoutWrapperReservationsSideNav.onholdBadgeAriaLabel' },
            { count: onholdReservationCount }
          )}
        >
          {onholdReservationCount > 99 ? '99+' : onholdReservationCount}
        </span>
      </span>
    ) : (
      <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.onHoldListingsTabTitle" />
    );

  const tabs = [
    {
      text: onholdTabLabel,
      selected: currentPage === 'HoldListingsPage',
      id: 'HoldListingsPageTab',
      linkProps: {
        name: 'HoldListingsPage',
      },
    },
    {
      text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.upcomingListingsTabTitle" />,
      selected: currentPage === 'UpcomingListingsPage',
      id: 'UpcomingListingsPageTab',
      linkProps: {
        name: 'UpcomingListingsPage',
      },
    },
    {
      text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.pastListingsTabTitle" />,
      selected: currentPage === 'PastListingsPage',
      id: 'PastListingsPageTab',
      linkProps: {
        name: 'PastListingsPage',
      },
    },
    ...(userCanSeeLegacyReservaAntiguas(ensuredUser)
      ? [
          {
            text: (
              <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.ReservaAntiguasPageTabTitle" />
            ),
            selected: currentPage === 'ReservaAntiguesPage',
            id: 'ReservaAntiguesPageTab',
            linkProps: {
              name: 'ReservaAntiguesPage',
            },
          },
        ]
      : []),
    ...(userHasAnnualStats(ensuredUser)
      ? [
          {
            text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.ingresosTabTitle" />,
            selected: currentPage === 'IngresosPage',
            id: 'IngresosPageTab',
            linkProps: {
              name: 'IngresosPage',
            },
          },
        ]
      : []),
  ];

  //   if(isProvider){
  //     tabs.push({
  //       text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.paymentsTabTitle" />,
  //       selected: currentPage === 'StripePayoutPage',
  //       id: 'StripePayoutPageTab',
  //       linkProps: {
  //         name: 'StripePayoutPage',
  //       },
  //     });
  //   }

  return (
    <>
      <TabNav rootClassName={css.tabs} tabRootClassName={css.tab} tabs={tabs} />
      {/* "Proteger reserva externa" belongs to the reservations NAV, not to any one of
          its tabs. It used to be passed in as `sideNav` from UpcomingListingsPage alone,
          which meant the <aside> — content-sized — was wider on Próximas than on the
          other tabs, and the whole column jumped on every tab change.

          The slot is reserved for every host, without waiting on the availability fetch:
          that fetch is what decides whether the card renders, so gating the slot on it
          sized the column twice on load. ExternalBookingEntry renders nothing when the
          feature is unavailable, leaving an empty (invisible) slot only on a deployment
          without DATABASE_URL. */}
      {isProvider ? (
        <div className={css.externalBookingsSlot}>
          <ExternalBookingEntry id="ReservationsSideNav" variant="card" />
        </div>
      ) : null}
    </>
  );
};

LayoutWrapperReservationsSideNavComponent.defaultProps = {
  className: null,
  rootClassName: null,
  children: null,
  currentPage: null,
};

LayoutWrapperReservationsSideNavComponent.propTypes = {
  children: node,
  className: string,
  rootClassName: string,
  currentPage: string,
  onholdReservationCount: number.isRequired,
  onFetchOnholdReservations: func.isRequired,

  // from withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const refs = state.OnholdListingsPage?.transactionRefs ?? [];
  const transactions = getMarketplaceEntities(state, refs);
  return {
    currentUser,
    onholdReservationCount: transactions.length,
  };
};

const mapDispatchToProps = dispatch => ({
  onFetchOnholdReservations: (tab, search) => dispatch(loadOnholdReservations({ tab }, search)),
});

const LayoutWrapperReservationsSideNav = compose(
  withViewport,
  connect(mapStateToProps, mapDispatchToProps)
)(LayoutWrapperReservationsSideNavComponent);

export default LayoutWrapperReservationsSideNav;
